import { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../api/axios";
import { useCart } from "../context/CartContext";
import Footer from "../components/Footer";

const DUMMY_PRODUCTS = [
  { id: 1, name: "Amber & Oud",       price: "799", category: { name: "Woody" },  description: "Deep, resinous warmth with smoky amber base notes.", image: null },
  { id: 2, name: "Rose Petals",       price: "699", category: { name: "Floral" }, description: "Fresh rose with soft powdery undertones.",           image: null },
  { id: 3, name: "Ocean Breeze",      price: "649", category: { name: "Fresh" },  description: "Crisp sea air and light citrus for an airy feel.",   image: null },
  { id: 4, name: "Spiced Vanilla",    price: "749", category: { name: "Spice" },  description: "Warm cinnamon, clove, and rich vanilla bean.",       image: null },
  { id: 5, name: "Sandalwood Dreams", price: "849", category: { name: "Woody" },  description: "Creamy sandalwood with a hint of warm musk.",        image: null },
  { id: 6, name: "Jasmine Bloom",     price: "729", category: { name: "Floral" }, description: "Lush jasmine with delicate white floral heart.",     image: null },
  { id: 7, name: "Eucalyptus Mint",   price: "669", category: { name: "Fresh" },  description: "Clean eucalyptus and cool peppermint blend.",        image: null },
  { id: 8, name: "Chai Spice",        price: "779", category: { name: "Spice" },  description: "Cardamom, ginger, and black tea — like a warm hug.", image: null },
];

// Palette for known category names
const KNOWN_PALETTE = {
  Woody:  { bg: "#e8d9c4", accent: "#9b6e3a", emoji: "🪵" },
  Floral: { bg: "#f2e0e8", accent: "#c4607a", emoji: "🌸" },
  Fresh:  { bg: "#d8eee6", accent: "#3a8a68", emoji: "🍃" },
  Spice:  { bg: "#f0e0cc", accent: "#c47a3a", emoji: "🌶️" },
};

// Generic palettes for any new category added via admin
const GENERIC_PALETTES = [
  { bg: "#dde8f2", accent: "#3a6ab0", emoji: "🕯️" },
  { bg: "#e8e0f2", accent: "#7a3ab0", emoji: "✨" },
  { bg: "#f2ead8", accent: "#b07a3a", emoji: "🌿" },
  { bg: "#d8f2ec", accent: "#2a8a78", emoji: "💫" },
];

const getPalette = (categoryName) => {
  if (!categoryName) return { bg: "#f0ebe3", accent: "#888", emoji: "🕯️" };
  if (KNOWN_PALETTE[categoryName]) return KNOWN_PALETTE[categoryName];
  const idx = categoryName.charCodeAt(0) % GENERIC_PALETTES.length;
  return GENERIC_PALETTES[idx];
};

const inr = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

const getCategoryName = (category) => {
  if (!category) return "";
  if (typeof category === "object") return category.name || "";
  return String(category);
};

export default function Products() {
  const [products, setProducts]             = useState([]);
  const [categories, setCategories]         = useState(["All"]);  // dynamic from API
  const [loading, setLoading]               = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");
  const [loginPrompt, setLoginPrompt]       = useState(false);
  const [searchParams]                      = useSearchParams();
  const { addToCart }                       = useCart();

  const showLogin = () => {
    setLoginPrompt(true);
    setTimeout(() => setLoginPrompt(false), 3000);
  };

  // Sync ?category= query param to active pill
  useEffect(() => {
    const cat = searchParams.get("category");
    if (cat) setActiveCategory(cat.charAt(0).toUpperCase() + cat.slice(1));
  }, [searchParams]);

  // Fetch products AND categories in parallel
  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("products/"),
      api.get("products/categories/").catch(() => ({ data: [] })),
    ]).then(([prodRes, catRes]) => {
      const prodData = prodRes.data?.results || prodRes.data || [];
      setProducts(prodData.length > 0 ? prodData : DUMMY_PRODUCTS);

      const catData = catRes.data || [];
      if (catData.length > 0) {
        // Build ["All", "Floral", "Woody", ...] from whatever is in DB
        setCategories(["All", ...catData.map(c => c.name)]);
      } else {
        // Fallback: derive unique names from product list itself
        const names = [...new Set(
          prodData.map(p => getCategoryName(p.category)).filter(Boolean)
        )];
        setCategories(names.length > 0 ? ["All", ...names] : ["All"]);
      }
    }).catch(() => {
      setProducts(DUMMY_PRODUCTS);
      setCategories(["All", "Floral", "Woody", "Fresh", "Spice"]);
    }).finally(() => setLoading(false));
  }, []);

  const filtered = products.filter((p) =>
    activeCategory === "All" ||
    getCategoryName(p.category).toLowerCase() === activeCategory.toLowerCase()
  );

  return (
    <div style={{ background: "#faf7f2", minHeight: "100vh", paddingTop: "64px" }}>

      {/* Login toast */}
      {loginPrompt && (
        <div
          className="fixed top-20 left-1/2 z-50 flex items-center gap-3 rounded-xl px-5 py-3 shadow-xl"
          style={{ transform: "translateX(-50%)", background: "#1a1a1a", color: "#fff",
            fontFamily: "Georgia, serif", fontSize: "13px", whiteSpace: "nowrap",
            animation: "toastUp 0.3s ease", zIndex: 9999 }}>
          <span>🔒</span> Please log in first.
          <button
            onClick={() => window.location.href = "/login"}
            className="rounded-full px-3 py-1 text-xs font-semibold ml-1"
            style={{ background: "#e07b39", color: "#fff", border: "none", cursor: "pointer", fontFamily: "Georgia, serif" }}>
            Log In
          </button>
        </div>
      )}

      {/* Page header */}
      <div className="w-full text-center" style={{ padding: "60px 6% 36px", background: "#fff", borderBottom: "1px solid #f0ebe3" }}>
        <p style={{ fontFamily: "Georgia, serif", color: "#e07b39", fontSize: "11px",
          letterSpacing: "0.2em", textTransform: "uppercase", marginBottom: "10px" }}>
          Our Collection
        </p>
        <h1 className="font-bold" style={{ fontFamily: "Georgia, serif", fontSize: "clamp(28px, 4vw, 44px)", color: "#1a1a1a", marginBottom: "10px" }}>
          Shop All Candles
        </h1>
        <p style={{ fontFamily: "Georgia, serif", fontSize: "13px", color: "#aaa" }}>
          {loading ? "Loading…" : `${filtered.length} product${filtered.length !== 1 ? "s" : ""} found`}
        </p>
      </div>

      <div style={{ padding: "0 6% 80px", maxWidth: "1360px", margin: "0 auto" }}>

        {/* Category pills — fully dynamic from API */}
        <div className="flex flex-wrap items-center gap-2 py-6" style={{ borderBottom: "1px solid #f0ebe3" }}>
          {categories.map((cat) => (
            <button key={cat} onClick={() => setActiveCategory(cat)}
              className="rounded-full text-xs font-semibold tracking-wide transition-all duration-200"
              style={{ padding: "8px 20px", fontFamily: "Georgia, serif",
                background: activeCategory === cat ? "#e07b39" : "#fff",
                color:      activeCategory === cat ? "#fff"    : "#666",
                border:     `1.5px solid ${activeCategory === cat ? "#e07b39" : "#e8dfd0"}`,
                cursor: "pointer", letterSpacing: "0.06em" }}>
              {cat}
            </button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <Skeleton />
        ) : filtered.length === 0 ? (
          <div className="text-center py-24" style={{ fontFamily: "Georgia, serif", color: "#aaa", fontSize: "14px" }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🕯️</div>
            No candles found in this category.
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-8">
            {filtered.map((p, i) => (
              <ProductCard key={p.id} product={p} index={i} addToCart={addToCart} onLoginRequired={showLogin} />
            ))}
          </div>
        )}
      </div>

      <Footer />

      <style>{`@keyframes toastUp { from{opacity:0;transform:translateX(-50%) translateY(10px)} to{opacity:1;transform:translateX(-50%) translateY(0)} }`}</style>
    </div>
  );
}

// ── Product Card ──────────────────────────────────────────────────────────────
function ProductCard({ product, index, addToCart, onLoginRequired }) {
  const [hovered, setHovered] = useState(false);
  const [added, setAdded]     = useState(false);
  const isLoggedIn = !!localStorage.getItem("token");

  const categoryName = getCategoryName(product.category);
  const palette      = getPalette(categoryName);

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isLoggedIn) { onLoginRequired(); return; }
    addToCart(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  const isDummy = !product.id;

  return (
    <div
      className="flex flex-col rounded-2xl overflow-hidden transition-all duration-300"
      style={{
        background:  "#fff",
        boxShadow:   hovered ? "0 16px 48px rgba(0,0,0,0.11)" : "0 2px 12px rgba(0,0,0,0.05)",
        transform:   hovered ? "translateY(-5px)" : "translateY(0)",
        animation:   `cardFade 0.45s ease ${index * 0.06}s both`,
        cursor:      "pointer",
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>

      <Link
        to={isDummy ? "#" : `/products/${product.id}`}
        style={{ textDecoration: "none", color: "inherit", display: "flex", flexDirection: "column", flex: 1 }}
        onClick={isDummy ? (e) => e.preventDefault() : undefined}>

        <div className="relative overflow-hidden flex items-center justify-center"
          style={{ aspectRatio: "1/1", background: palette.bg }}>
          {product.image
            ? <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-500"
                style={{ transform: hovered ? "scale(1.06)" : "scale(1)" }} />
            : <CandleSVG palette={palette} hovered={hovered} />
          }
          <div className="absolute top-3 left-3 text-xs rounded-full px-2.5 py-1"
            style={{ background: "rgba(255,255,255,0.88)", color: palette.accent,
              fontFamily: "Georgia, serif", fontSize: "10px", fontWeight: "600" }}>
            {categoryName || "Candle"}
          </div>
          {!isDummy && (
            <div style={{
              position: "absolute", bottom: 0, left: 0, right: 0,
              background: "linear-gradient(to top, rgba(26,26,26,0.55), transparent)",
              padding: "24px 12px 10px",
              opacity: hovered ? 1 : 0,
              transition: "opacity 0.25s ease",
            }}>
              <span style={{ fontFamily: "Georgia, serif", fontSize: "11px", color: "#fff", letterSpacing: "0.06em" }}>
                View Details →
              </span>
            </div>
          )}
        </div>

        <div className="flex flex-col flex-1 p-4" style={{ paddingBottom: "10px" }}>
          <div className="font-semibold mb-1"
            style={{ fontFamily: "Georgia, serif", color: "#1a1a1a", fontSize: "14px", lineHeight: "1.3" }}>
            {product.name}
          </div>
          {product.description && (
            <div className="text-xs mb-3"
              style={{ fontFamily: "Georgia, serif", color: "#999", lineHeight: "1.6",
                overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
              {product.description}
            </div>
          )}

          <div className="font-bold mb-2"
            style={{ fontFamily: "Georgia, serif", color: "#1a1a1a", fontSize: "17px" }}>
            {inr(parseFloat(product.price || 0))}
          </div>
        </div>
      </Link>

      <div style={{ padding: "0 16px 16px" }}>
        <button onClick={handleAdd}
          className="w-full rounded-full text-xs font-semibold transition-all duration-200"
          style={{ padding: "11px 0", background: added ? "#3a8a68" : "#e07b39", color: "#fff",
            border: "none", cursor: "pointer", fontFamily: "Georgia, serif", letterSpacing: "0.06em",
            boxShadow: !added && hovered ? "0 4px 14px rgba(224,123,57,0.38)" : "none" }}>
          {added ? "Added to Cart ✓" : !isLoggedIn ? "🔒 Log in to Add" : "+ Add to Cart"}
        </button>
      </div>

      <style>{`@keyframes cardFade { from{opacity:0;transform:translateY(18px)} to{opacity:1;transform:translateY(0)} }`}</style>
    </div>
  );
}

function CandleSVG({ palette, hovered }) {
  return (
    <div className="flex flex-col items-center justify-center w-full h-full transition-transform duration-500"
      style={{ transform: hovered ? "scale(1.04)" : "scale(1)" }}>
      <svg width="72" height="110" viewBox="0 0 72 110" fill="none">
        <ellipse cx="36" cy="18" rx="10" ry="14" fill={palette.accent} opacity="0.18" />
        <path d="M36 6C36 6 42 14 42 20C42 23.3 39.3 26 36 26C32.7 26 30 23.3 30 20C30 14 36 6 36 6Z" fill={palette.accent} opacity="0.9" />
        <path d="M36 15C36 15 38.5 18.5 38.5 21C38.5 22.4 37.4 23.5 36 23.5C34.6 23.5 33.5 22.4 33.5 21C33.5 18.5 36 15 36 15Z" fill="white" opacity="0.7" />
        <line x1="36" y1="26" x2="36" y2="32" stroke="#555" strokeWidth="1.5" strokeLinecap="round" />
        <rect x="16" y="32" width="40" height="5" rx="2.5" fill={palette.accent} opacity="0.5" />
        <rect x="18" y="37" width="36" height="58" rx="6" fill="white" />
        <rect x="18" y="37" width="36" height="58" rx="6" fill={palette.accent} opacity="0.12" />
        <rect x="22" y="55" width="28" height="24" rx="3" fill={palette.accent} opacity="0.18" />
        <rect x="16" y="93" width="40" height="5" rx="2.5" fill={palette.accent} opacity="0.4" />
      </svg>
      <span style={{ fontSize: "13px", marginTop: "4px", opacity: 0.5 }}>{palette.emoji}</span>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mt-8">
      {[...Array(8)].map((_, i) => (
        <div key={i} className="rounded-2xl overflow-hidden" style={{ background: "#fff" }}>
          <div style={{ aspectRatio: "1/1", background: "#f0ebe3", animation: "pulse 1.5s ease infinite" }} />
          <div className="p-4 flex flex-col gap-3">
            <div className="rounded" style={{ height: "14px", background: "#f0ebe3", width: "70%", animation: "pulse 1.5s ease infinite" }} />
            <div className="rounded" style={{ height: "11px", background: "#f0ebe3", width: "90%", animation: "pulse 1.5s ease infinite" }} />
            <div className="rounded-full" style={{ height: "34px", background: "#f0ebe3", animation: "pulse 1.5s ease infinite" }} />
          </div>
        </div>
      ))}
      <style>{`@keyframes pulse{0%,100%{opacity:1}50%{opacity:0.5}}`}</style>
    </div>
  );
}